import posthog from "posthog-js";

// The browser half of analytics (spec: docs/project/analytics-spec.md). The
// server is the source of truth for product events (events/analytics.ts: the
// domain log, whitelisted, 100 % of users); this side is best effort — an ad
// blocker may drop all of it — and owns what only a browser has: session
// replay, pageviews and autocapture, exceptions, the identity handshake, and
// the site's sign-in/up clicks that happen before any server sees the visitor
// (web/site/site.ts imports this module for them).
// Identity is the user's auth id (/api/me) on both ends, so the browser's identify()
// and the server's events land on one PostHog person. Everything posthog
// lives here; pages import track()/identifyUser(), never posthog-js.
//
// Config comes from the meta tag server/app.ts templates into index.html:
// an empty token (unset env) means off, and every function here is a no-op.

const PROD_UI = "https://us.posthog.com";

let on = false;

/** Reads the templated meta and initialises the SDK; idempotent, no-op without a token. */
export function startAnalytics(): void {
  if (on) return;
  const meta = document.querySelector('meta[name="featurette-posthog"]');
  const token = meta?.getAttribute("content")?.trim();
  const host = meta?.getAttribute("data-host")?.trim();
  if (!token || !host) return;
  posthog.init(token, {
    // The managed reverse proxy; ui_host keeps the toolbar/links on PostHog itself.
    api_host: host,
    ui_host: PROD_UI,
    defaults: "2026-05-30",
    // Anonymous visitors are persons too, so the site → portal → back
    // funnel merges into the identified person on identify().
    person_profiles: "always",
    // Our router is pushState (web/src/router.ts): the SDK sees each navigation.
    capture_pageview: "history_change",
    capture_exceptions: true,
    // Inputs are masked everywhere; the credentials card is masked whole
    // (class ph-no-capture on the form).
    session_recording: { maskAllInputs: true },
  });
  posthog.register({ appVersion: meta?.getAttribute("data-version") ?? "" });
  on = true;
}

export interface Person {
  email: string;
  name: string;
  orgId: string;
  orgName: string;
}

/** Ties this browser to the user by auth id (the server identifies its events the same way). */
export function identifyUser(authId: string, person: Person): void {
  if (on) posthog.identify(authId, person);
}

/** Sign-out: a fresh anonymous id, so the next person on this browser is not this one. */
export function resetUser(): void {
  if (on) posthog.reset();
}

/** A browser-only product event. `instant` skips the batch — for a capture right before a
 * navigation away (the site's sign-in/up clicks). */
export function track(
  event: string,
  properties: Record<string, unknown> = {},
  opts: { instant?: boolean } = {},
): void {
  if (on) posthog.capture(event, properties, opts.instant ? { send_instantly: true } : undefined);
}

/** An error we showed the user (web/src/errors.ts), alongside the SDK's own autocapture. */
export function trackException(e: unknown): void {
  if (on) posthog.captureException(e);
}
